import {
  type SessionTaskAttempt,
  SessionTaskAttemptResultEnum,
} from "~/lib/objects";
import { calculateRatingDeltaFromLife } from "~/utils/ratingCalculations";

export type ProblemSetStats = {
  winCount: number;
  drawCount: number;
  lossCount: number;
  solvedCount: number;
  ratingDelta: number;
  retryNumber: number;
  unsolvedCount: number;
};

export function computeProblemSetStats(
  attempts: SessionTaskAttempt[],
  problems: Array<{ problemId: number }>,
): ProblemSetStats {
  const problemIds = new Set(problems.map((p) => p.problemId));

  // Map of latest main attempt per problemId
  const mainAttempts = new Map<number, SessionTaskAttempt>();

  for (const a of attempts) {
    if (!problemIds.has(a.problemId)) continue;

    const isMain =
      a.mainAttemptId === null ||
      a.mainAttemptId === undefined ||
      a.mainAttemptId === 0;
    if (!isMain) continue;

    // keep the last main attempt encountered for the problem
    mainAttempts.set(a.problemId, a);
  }

  let winCount = 0;
  let drawCount = 0;
  let lossCount = 0;
  let ratingDelta = 0;
  let retryNumber = 0;

  for (const [, a] of mainAttempts) {
    ratingDelta += a.pointsChange ?? 0;

    const res = a.finalResult;
    if (a.finalExternalHint) {
      drawCount++;
    } else if (res === SessionTaskAttemptResultEnum.WIN) {
      winCount++;
    } else if (res === SessionTaskAttemptResultEnum.DRAW) {
      drawCount++;
    } else if (res === SessionTaskAttemptResultEnum.LOSS) {
      lossCount++;
    }

    if ((a.retryCountUsed ?? 0) > 0) retryNumber++;
  }

  const solvedCount = winCount + drawCount;
  let unsolvedCount = problems.length - solvedCount - lossCount;
  if (unsolvedCount < 0) unsolvedCount = 0;

  return {
    winCount,
    drawCount,
    lossCount,
    solvedCount,
    ratingDelta,
    retryNumber,
    unsolvedCount,
  };
}

const DEFAULT_PROBLEM_RATING = 1000;

export function computeMaxRatingPercent(
  problems: Array<{
    problemId: number;
    rating: number | null;
    ratingDelta: number;
    result: SessionTaskAttemptResultEnum;
    finalExternalHint?: boolean;
    retryCountUsed?: number;
  }>,
  lifeRating: number,
): {
  smax: number;
  scurr: number;
  percent: number;
  smaxSolved: number;
  percentSolved: number;
  scurrSolved: number;
} {
  let smax = 0;
  let scurr = 0;
  let smaxSolved = 0;
  let scurrSolved = 0;

  for (const p of problems) {
    const taskRating = p.rating ?? DEFAULT_PROBLEM_RATING;

    // Smax should include all problems in the set to represent total possible progress
    const winDelta = calculateRatingDeltaFromLife(
      lifeRating,
      taskRating,
      SessionTaskAttemptResultEnum.WIN,
      0,
      40,
    );
    smax += winDelta;

    // Use actual stored rating delta from attempt for current points (preserve historical values)
    scurr += p.ratingDelta;

    if (
      !p.finalExternalHint &&
      p.result !== SessionTaskAttemptResultEnum.WIN &&
      p.result !== SessionTaskAttemptResultEnum.DRAW
    ) {
      continue;
    }

    scurrSolved += p.ratingDelta;
    // This problem is solved - add its potential max to smaxSolved
    smaxSolved += winDelta;
  }

  const percent = smax <= 0 ? 0 : (scurr / smax) * 100;
  const percentSolved =
    smaxSolved <= 0 ? 0 : (scurrSolved / smaxSolved) * 100;

  return { smax, scurr, percent, smaxSolved, percentSolved, scurrSolved };
}

export function computeMaxRatingPercentFromAttempts(
  attempts: SessionTaskAttempt[],
  problems: Array<{ problemId: number; rating: number | null }>,
  lifeRating: number,
): { percent: number; percentSolved: number } {
  // Map of main attempt per problemId
  const mainAttempts = new Map<number, SessionTaskAttempt>();
  for (const a of attempts) {
    const isMain =
      a.mainAttemptId === null ||
      a.mainAttemptId === undefined ||
      a.mainAttemptId === 0;
    if (!isMain) continue;
    mainAttempts.set(a.problemId, a);
  }

  const mapped = problems.map((p) => {
    const ma = mainAttempts.get(p.problemId);
    const result = ma ? ma.finalResult : SessionTaskAttemptResultEnum.PENDING;

    // if (
    //   ma &&
    //   ma.finalResult === SessionTaskAttemptResultEnum.WIN &&
    //   (ma.hintUsed || ma.finalExternalHint)
    // ) {
    //   result = SessionTaskAttemptResultEnum.DRAW;
    // }

    return {
      problemId: p.problemId,
      rating: p.rating,
      ratingDelta: ma ? (ma.pointsChange ?? 0) : 0,
      finalExternalHint: ma ? (ma.finalExternalHint ?? false) : false,
      retryCountUsed: ma ? (ma.retryCountUsed ?? 0) : 0,
      result:
        ma && ma.finalExternalHint
          ? SessionTaskAttemptResultEnum.DRAW
          : result,
    } as const;
  });

  const result = computeMaxRatingPercent(mapped, lifeRating);
  return { percent: result.percent, percentSolved: result.percentSolved };
}
